import React, { useState, useEffect, useRef } from 'react';
import { Clock, CheckCircle, ChevronLeft, ChevronRight, Brain, Sparkles, X, RotateCcw, Search, Filter, Target, Trophy } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useProgress } from '../context/ProgressContext';

const Aptitude = () => {
  const { user } = useAuth();
  const { 
    aptitudeSolved, 
    markAptitudeSolved, 
    resetAptitudeQuestion, 
    aptitudeAnswers, 
    setAptitudeAnswers 
  } = useProgress();
  const [filterCategory, setFilterCategory] = useState('quant');
  const [filterTopic, setFilterTopic] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [questions, setQuestions] = useState([]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [solvedCountInCat, setSolvedCountInCat] = useState(0);

  const handleMarkAptitudeSolved = (id, categoryKey) => {
    if (aptitudeSolved.has(id)) return;
    markAptitudeSolved(id, categoryKey);
    setSolvedCountInCat(prev => prev + 1);
  };

  const handleResetAptitudeQuestion = (id, categoryKey) => {
    if (!aptitudeSolved.has(id)) return;
    resetAptitudeQuestion(id, categoryKey);
    setSolvedCountInCat(prev => Math.max(0, prev - 1));
  };
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  const debounceRef = useRef(null);

  const categories = [
    { id: 'quant', label: 'Quantitative', emoji: '🔢' },
    { id: 'reasoning', label: 'Reasoning', emoji: '🧩' },
    { id: 'verbal', label: 'Verbal Ability', emoji: '📝' },
    { id: 'di', label: 'Data Interpretation', emoji: '📊' }
  ];

  const topicsMap = {
    quant: ['All', 'Simple Interest', 'Time & Distance', 'Ratio & Proportion', 'Profit & Loss', 'Percentage', 'Number System', 'Averages', 'Time & Work', 'General'],
    reasoning: ['All', 'Blood Relations', 'Coding-Decoding', 'Number Series', 'Syllogism', 'Seating Arrangement', 'General'],
    verbal: ['All', 'Synonyms', 'Antonyms', 'Grammar', 'Fill in the blanks', 'General'],
    di: ['All', 'Data Interpretation', 'General']
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const res = await api.getAptitudeQuestions({ category: filterCategory, topic: filterTopic, page, limit, userId: user?.id });
      const data = await res.json();
      if (res.ok) {
        setQuestions(data.data);
        setTotalPages(data.totalPages || 1);
        setTotalQuestions(data.total || 0);
        setSolvedCountInCat(data.solvedInCategory || 0);
      }
    } catch (error) {
      console.error('Failed to fetch aptitude questions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchQuestions();
    }, 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCategory, filterTopic, page]);

  useEffect(() => {
    setPage(1);
    setFilterTopic('All');
  }, [filterCategory]);

  useEffect(() => {
    setPage(1);
  }, [filterTopic]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  const handleSelectOption = (questionId, optionIndex) => {
    setAptitudeAnswers(prev => ({
      ...prev,
      [questionId]: optionIndex
    }));
  };

  const filteredQuestions = questions.filter(q => 
    q.question.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex flex-col lg:flex-row gap-8 items-start">
      {/* Sidebar Filter */}
      <aside className="w-full lg:w-72 flex-shrink-0 space-y-6 lg:sticky lg:top-24 lg:h-[calc(100vh-120px)] overflow-y-auto pb-10 pr-2 custom-scrollbar">
        <div className="glass-card p-6 space-y-8">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Filter className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white font-display uppercase tracking-wider text-sm">Filter by Topic</h3>
          </div>

          {/* Search Box */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input 
              type="text"
              placeholder="Search aptitude questions..."
              className="input-field pl-10 pr-4 py-2.5 text-sm w-full bg-gray-50/50 dark:bg-gray-800/50 focus:bg-white dark:focus:bg-gray-800 transition-all border-gray-200 dark:border-gray-700"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Category Section */}
          <div className="space-y-4">
            <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Section</p>
            <div className="flex flex-wrap lg:flex-col gap-2 no-scrollbar overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setFilterCategory(cat.id)}
                  className={`whitespace-nowrap px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 text-left flex items-center gap-3 group ${
                    filterCategory === cat.id 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/20' 
                      : 'bg-gray-50 dark:bg-gray-800/40 text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 border border-transparent hover:border-purple-100 dark:hover:border-purple-900/50'
                  }`}
                >
                  <span className={filterCategory === cat.id ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'}>{cat.emoji}</span>
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Topic Section */}
          <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-gray-800/50">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Available Topics</p>
              <span className="text-[10px] font-medium text-purple-500 bg-purple-50 dark:bg-purple-900/30 px-2 py-0.5 rounded-full">
                {filterTopic}
              </span>
            </div>
            <div className="flex flex-wrap lg:flex-col gap-1.5 no-scrollbar overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
              {topicsMap[filterCategory].map(topic => (
                <button
                  key={topic}
                  onClick={() => setFilterTopic(topic)}
                  className={`whitespace-nowrap px-4 py-2 rounded-xl text-xs font-bold transition-all duration-300 text-left flex items-center justify-between group ${
                    filterTopic === topic 
                      ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-md' 
                      : 'text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 hover:text-purple-500'
                  }`}
                >
                  {topic}
                  {filterTopic === topic && (
                    <Sparkles className="w-3 h-3 animate-pulse" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 w-full space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-2">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white font-display leading-tight uppercase">Aptitude Arena</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-tight">Sharpen your logic with category-specific drills.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-4 py-2 rounded-xl border border-amber-200 dark:border-amber-800/50 uppercase tracking-wider">
            <Clock className="w-3 h-3" />
            <span>Timed Practice</span>
          </div>
        </div>

        {/* Stats Row */}
        {user && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
            <div className="glass-card p-4 flex items-center gap-4 shadow-sm border border-emerald-100 dark:border-emerald-900/30">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{aptitudeSolved.size}</p>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Mastery</p>
              </div>
            </div>
            <div className="glass-card p-4 flex items-center gap-4 shadow-sm border border-purple-100 dark:border-purple-900/30">
              <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Target className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalQuestions}</p>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Category Questions</p>
              </div>
            </div>
            <div className="glass-card p-4 flex flex-col justify-center shadow-sm">
              <div className="flex justify-between items-center mb-2">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Category Mastery</span>
                <span className="text-xs font-bold text-purple-500">
                  {Math.round((solvedCountInCat / (totalQuestions > 0 ? totalQuestions : 1) || 0) * 100)}%
                </span>
              </div>
              <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-1000" 
                  style={{ width: `${(solvedCountInCat / (totalQuestions > 0 ? totalQuestions : 1) || 0) * 100}%` }}
                />
              </div>
              <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 mt-2 text-right">
                {solvedCountInCat} / {totalQuestions} solved
              </p>
            </div>
          </div>
        )}

        {/* Questions */}
        <div className="space-y-6">
          {loading ? (
            <div className="glass-card flex flex-col items-center justify-center p-20 space-y-4">
              <div className="relative">
                <div className="w-14 h-14 border-4 border-purple-100 dark:border-purple-900/50 rounded-full" />
                <div className="absolute inset-0 w-14 h-14 border-4 border-transparent border-t-purple-500 rounded-full animate-spin" />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Fetching challenges...</p>
            </div>
          ) : (
            <>
              {filteredQuestions.map((q, idx) => {
                const selected = aptitudeAnswers[q.id];
                const isAnswered = selected !== undefined;
                const isCorrect = selected === q.correctIndex;
                const isSolvedGlobally = aptitudeSolved.has(q.id);
                const globalIdx = (page - 1) * limit + idx + 1;

                return (
                  <div 
                    key={q.id} 
                    className={`glass-card p-6 md:p-8 transition-all duration-300 group hover:shadow-xl ${
                      isSolvedGlobally 
                        ? 'border-l-4 border-l-emerald-500 bg-emerald-50/10' 
                        : isAnswered && !isCorrect 
                          ? 'border-l-4 border-l-red-400 bg-red-50/10' 
                          : ''
                    }`}
                  >
                    <div className="flex justify-between items-start mb-6 gap-4">
                      <h4 className="text-base font-bold text-gray-900 dark:text-white leading-relaxed">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-extrabold mr-3 shadow-md align-middle">
                          {globalIdx}
                        </span>
                        {q.question}
                      </h4>
                      <div className="flex items-center gap-2">
                        {isAnswered && (
                          <button
                            onClick={() => handleResetAptitudeQuestion(q.id, filterCategory)}
                            title="Re-attempt question"
                            className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-400 hover:text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-all flex items-center justify-center border border-gray-200 dark:border-gray-700"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                        {isSolvedGlobally && (
                          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {q.options.map((opt, oIdx) => {
                        const isSelected = selected === oIdx;
                        let optionClass = 'border-gray-100 dark:border-gray-800 hover:border-purple-300 dark:hover:border-purple-600 hover:bg-purple-50/20 dark:hover:bg-purple-900/10 cursor-pointer';
                        let iconElement = null;
                        
                        if (isAnswered) {
                          if (oIdx === q.correctIndex) {
                            optionClass = 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-400 dark:border-emerald-600 text-emerald-800 dark:text-emerald-300';
                            iconElement = <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />;
                          } else if (isSelected) {
                            optionClass = 'bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-600 text-red-800 dark:text-red-300';
                            iconElement = <X className="w-5 h-5 text-red-500 flex-shrink-0" />;
                          } else {
                            optionClass = 'opacity-40 border-gray-100 dark:border-gray-800 cursor-default';
                          }
                        }

                        return (
                          <button
                            key={oIdx}
                            disabled={isAnswered}
                            onClick={() => {
                              handleSelectOption(q.id, oIdx);
                              if (oIdx === q.correctIndex) handleMarkAptitudeSolved(q.id, filterCategory);
                            }}
                            className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all duration-300 font-semibold text-sm flex items-center justify-between gap-3 ${optionClass} ${
                              isAnswered ? '' : 'active:scale-[0.98]'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                                isAnswered && oIdx === q.correctIndex
                                  ? 'bg-emerald-500 text-white'
                                  : isAnswered && isSelected && oIdx !== q.correctIndex
                                    ? 'bg-red-500 text-white'
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                              }`}>
                                {String.fromCharCode(65 + oIdx)}
                              </span>
                              <span>{opt}</span>
                            </div>
                            {iconElement}
                          </button>
                        );
                      })}
                    </div>

                    {isAnswered && (
                      <div className={`mt-6 p-6 rounded-2xl border-2 animate-slide-up-fade ${
                        isCorrect 
                          ? 'bg-emerald-50/30 border-emerald-100 dark:bg-emerald-900/5 dark:border-emerald-800/30' 
                          : 'bg-red-50/30 border-red-100 dark:bg-red-900/5 dark:border-red-800/30'
                      }`}>
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg ${
                            isCorrect ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
                          }`}>
                            {isCorrect ? <Sparkles className="w-5 h-5" /> : <X className="w-5 h-5" />}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-widest mb-1">
                              {isCorrect ? 'Victory! Right on point' : 'A Learning Opportunity'}
                            </p>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                              Correct Answer: <span className="font-extrabold text-gray-900 dark:text-white px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded mx-1">{q.answer}</span>
                            </p>
                            {q.explanation && (
                              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800/50">
                                <span className="text-[10px] font-black text-purple-500 uppercase tracking-widest mb-2 block">Deep Dive Explanation</span>
                                <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed italic border-l-4 border-purple-500/20 pl-4 py-1">
                                  {q.explanation}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {filteredQuestions.length === 0 && (
                <div className="glass-card p-20 text-center space-y-4 shadow-inner bg-gray-50/30">
                  <div className="w-16 h-16 bg-white dark:bg-gray-800 rounded-3xl flex items-center justify-center mx-auto shadow-sm">
                    <Search className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Quest Not Found</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Refine your filters to discover more challenges.</p>
                  </div>
                </div>
              )}
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="glass-card p-6 flex items-center justify-between shadow-lg">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-gray-400 disabled:opacity-30 hover:text-purple-600 transition-all px-4 py-2 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-xl"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    PREVIOUS
                  </button>
                  <div className="hidden sm:flex items-center gap-2">
                    {[...Array(totalPages)].map((_, i) => {
                      const pageNum = i + 1;
                      if (pageNum === 1 || pageNum === totalPages || (pageNum >= page - 1 && pageNum <= page + 1)) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setPage(pageNum)}
                            className={`w-10 h-10 rounded-xl text-sm font-extrabold transition-all border-2 ${
                              page === pageNum
                                ? 'bg-gray-900 border-gray-900 text-white shadow-lg'
                                : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-800 text-gray-400 hover:border-purple-300'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      }
                      if (pageNum === page - 2 || pageNum === page + 2) {
                        return <span key={pageNum} className="text-gray-300 px-1 font-bold">...</span>;
                      }
                      return null;
                    })}
                  </div>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-gray-400 disabled:opacity-30 hover:text-purple-600 transition-all px-4 py-2 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-xl"
                  >
                    NEXT
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Aptitude;
